/* =========================================================
   Build-a-Bundle  —  juo-build-a-bundle.js
   ---------------------------------------------------------
   Vanilla JS. Multiple sections per page are supported.

   Cart integration (JUO):
     Each line item gets properties:
       _box: "box-<PARENT_PRODUCT_ID>"
       _parent_product_handle: "<handle>"
       _parent_product_id: "<id>"
     Subscription mode adds selling_plan per line.
   ========================================================= */

(function () {
  'use strict';

  var SELECTORS = {
    root:               '[data-bab-root]',
    config:             '[data-bab-config]',
    card:               '[data-bab-card]',
    addBtn:             '[data-bab-add]',
    variantSelect:      '[data-bab-variant-select]',
    navPrev:            '[data-bab-nav-prev]',
    navNext:            '[data-bab-nav-next]',
    items:              '[data-bab-items]',
    progressText:       '[data-bab-progress-text]',
    progressDiscount:   '[data-bab-progress-discount]',
    progressFill:       '[data-bab-progress-fill]',
    progressSegments:   '[data-bab-progress-segment]',
    total:              '[data-bab-total]',
    totalBar:           '[data-bab-total-bar]',
    sheetToggle:        '[data-bab-sheet-toggle]',
    summary:            '[data-bab-summary]',
    purchaseOptions:    '[data-bab-purchase-options]',
    purchaseOption:     '[data-bab-option]',
    purchaseRadio:      '[data-bab-purchase-radio]',
    frequencySelect:    '[data-bab-frequency-select]',
    frequencyPill:      '[data-bab-frequency-pill]',
    frequencyPills:     '[data-bab-frequency-pills]',
    planIdInput:        '[data-bab-plan-id]',
    idealToggle:        '[data-bab-ideal-toggle]',
    subscriptionBadge:  '[data-bab-subscription-badge]',
    onetimePrice:       '[data-bab-onetime-price]',
    subscriptionPrice:  '[data-bab-subscription-price]',
    originalPrice:      '[data-bab-original-price]',
    submit:             '[data-bab-submit]',
    submitLabel:        '[data-bab-submit-label]',
    error:              '[data-bab-error]',
    countBar:           '[data-bab-count-bar]'
  };

  /* ---------- money formatter ---------- */

  function formatMoney(cents, format) {
    var amount = cents / 100;
    var fmt = format || '${{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
      switch (key) {
        case 'amount':                              return amount.toFixed(2);
        case 'amount_no_decimals':                  return Math.round(amount).toString();
        case 'amount_with_comma_separator':         return amount.toFixed(2).replace('.', ',');
        case 'amount_no_decimals_with_comma_separator': return Math.round(amount).toString();
        case 'amount_with_space_separator':         return amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        default:                                    return amount.toFixed(2);
      }
    });
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function interpolate(template, data) {
    return template.replace(/\{(\w+)\}/g, function (_, key) {
      return data[key] != null ? data[key] : '';
    });
  }

  /* =========================================================
     Bundle instance
     ========================================================= */

  function createBundle(root) {
    var configEl = root.querySelector(SELECTORS.config);
    if (!configEl) { console.warn('[bab] missing config'); return; }

    var config;
    try { config = JSON.parse(configEl.textContent); }
    catch (e) { console.error('[bab] config parse failed', e); return; }

    config.tiers = (config.tiers || []).slice().sort(function (a, b) {
      return a.threshold - b.threshold;
    });

    var itemsEl          = root.querySelector(SELECTORS.items);
    var progressTextEl   = root.querySelector(SELECTORS.progressText);
    var progressDiscEl   = root.querySelector(SELECTORS.progressDiscount);
    var progressFillEl   = root.querySelector(SELECTORS.progressFill);
    var progressSegmentEls = Array.prototype.slice.call(root.querySelectorAll(SELECTORS.progressSegments));
    var totalEl          = root.querySelector(SELECTORS.total);
    var purchaseOptionsEl = root.querySelector(SELECTORS.purchaseOptions);
    var submitEl         = root.querySelector(SELECTORS.submit);
    var submitLabelEl    = root.querySelector(SELECTORS.submitLabel);
    var errorEl          = root.querySelector(SELECTORS.error);

    var moneyFormat = root.dataset.moneyFormat || config.moneyFormat || '${{amount}}';

    var state = {
      items:            new Map(),
      purchaseType:     'onetime',
      planId:           null,
      payWithIdeal:     false,
      planDiscount:     0,
      planDiscountType: 'percentage'
    };

    /* ---------- purchase options ---------- */

    function initPurchaseOptions() {
      if (!purchaseOptionsEl) return;

      // The iDEAL variant of a plan lives in data-plan-id-ideal; fall back to
      // the base id when the toggle is off or the frequency has no variant.
      function resolvePlanId(el, baseValue) {
        var raw = (state.payWithIdeal && el && el.dataset.planIdIdeal) ? el.dataset.planIdIdeal : baseValue;
        return parseInt(raw, 10) || null;
      }

      // Seed the iDEAL mode from the checkbox so a default-checked toggle
      // resolves to the iDEAL plan id right away
      var idealToggleEl = purchaseOptionsEl.querySelector(SELECTORS.idealToggle);
      state.payWithIdeal = !!(idealToggleEl && idealToggleEl.checked);

      // Seed plan from pill buttons, legacy select, or hidden input (single plan)
      var activePill = purchaseOptionsEl.querySelector(SELECTORS.frequencyPill + '.is-active')
                    || purchaseOptionsEl.querySelector(SELECTORS.frequencyPill);
      var freqSelect = purchaseOptionsEl.querySelector(SELECTORS.frequencySelect);
      var planInput  = purchaseOptionsEl.querySelector(SELECTORS.planIdInput);

      if (activePill) {
        state.planId           = resolvePlanId(activePill, activePill.dataset.planId);
        state.planDiscount     = parseInt(activePill.dataset.discount || '0', 10);
        state.planDiscountType = activePill.dataset.discountType || 'percentage';
      } else if (freqSelect && freqSelect.options.length > 0) {
        var selOpt = freqSelect.options[freqSelect.selectedIndex];
        state.planId           = parseInt(selOpt.value, 10) || null;
        state.planDiscount     = parseInt(selOpt.dataset.discount || '0', 10);
        state.planDiscountType = selOpt.dataset.discountType || 'percentage';
      } else if (planInput) {
        state.planId           = resolvePlanId(planInput, planInput.value);
        state.planDiscount     = parseInt(planInput.dataset.discount || '0', 10);
        state.planDiscountType = planInput.dataset.discountType || 'percentage';
      }

      purchaseOptionsEl.addEventListener('change', function (e) {
        // iDEAL toggle — re-resolve the current plan against the new payment mode
        if (e.target && e.target.matches(SELECTORS.idealToggle)) {
          state.payWithIdeal = e.target.checked;
          var currentPill = purchaseOptionsEl.querySelector(SELECTORS.frequencyPill + '.is-active')
                         || purchaseOptionsEl.querySelector(SELECTORS.frequencyPill);
          var currentInput = purchaseOptionsEl.querySelector(SELECTORS.planIdInput);
          if (currentPill) {
            state.planId = resolvePlanId(currentPill, currentPill.dataset.planId);
          } else if (currentInput) {
            state.planId = resolvePlanId(currentInput, currentInput.value);
          }
          render();
        }

        // Purchase type toggle
        if (e.target && e.target.matches(SELECTORS.purchaseRadio)) {
          state.purchaseType = e.target.value;
          purchaseOptionsEl.querySelectorAll(SELECTORS.purchaseOption).forEach(function (opt) {
            opt.classList.toggle('is-active', opt.contains(e.target));
          });
          render();
        }

        // Legacy frequency select (fallback)
        if (e.target && e.target.matches(SELECTORS.frequencySelect)) {
          var chosen = e.target.options[e.target.selectedIndex];
          state.planId           = parseInt(chosen.value, 10) || null;
          state.planDiscount     = parseInt(chosen.dataset.discount || '0', 10);
          state.planDiscountType = chosen.dataset.discountType || 'percentage';
          var badge = purchaseOptionsEl.querySelector(SELECTORS.subscriptionBadge);
          if (badge && state.planDiscount > 0) {
            badge.textContent = '-' + state.planDiscount + (state.planDiscountType === 'percentage' ? '%' : '');
          }
          render();
        }
      });

      // Frequency pill clicks
      purchaseOptionsEl.addEventListener('click', function (e) {
        var pill = e.target.closest(SELECTORS.frequencyPill);
        if (!pill) return;
        var group = pill.closest(SELECTORS.frequencyPills);
        if (group) {
          group.querySelectorAll(SELECTORS.frequencyPill).forEach(function (p) {
            p.classList.remove('is-active');
          });
        }
        pill.classList.add('is-active');
        state.planId           = resolvePlanId(pill, pill.dataset.planId);
        state.planDiscount     = parseInt(pill.dataset.discount || '0', 10);
        state.planDiscountType = pill.dataset.discountType || 'percentage';
        var badge = purchaseOptionsEl.querySelector(SELECTORS.subscriptionBadge);
        if (badge && state.planDiscount > 0) {
          badge.textContent = '-' + state.planDiscount + (state.planDiscountType === 'percentage' ? '%' : '');
        }
        render();
      });
    }

    /* ---------- card wiring ---------- */

    root.querySelectorAll(SELECTORS.card).forEach(function (card) {
      var addBtn       = card.querySelector(SELECTORS.addBtn);
      var variantSelect = card.querySelector(SELECTORS.variantSelect);

      if (variantSelect) {
        variantSelect.addEventListener('change', function () {
          var opt = variantSelect.options[variantSelect.selectedIndex];
          card.dataset.variantId    = opt.value;
          card.dataset.variantPrice = opt.dataset.price || '0';
        });
      }

      if (addBtn) {
        addBtn.addEventListener('click', function () { addToBundle(card); });
      }

      initCarousel(card);
    });

    /* ---------- carousel ---------- */

    function initCarousel(card) {
      var prevBtn = card.querySelector(SELECTORS.navPrev);
      var nextBtn = card.querySelector(SELECTORS.navNext);
      if (!prevBtn || !nextBtn) return;

      var images;
      try { images = JSON.parse(card.dataset.images || '[]'); }
      catch (e) { images = []; }
      if (images.length < 2) return;

      var idx   = 0;
      var imgEl = card.querySelector('.bab-card__image');

      function updateCarousel() {
        if (imgEl) imgEl.src = images[idx];
        prevBtn.hidden = idx === 0;
        nextBtn.hidden = idx === images.length - 1;
      }

      prevBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (idx > 0) { idx--; updateCarousel(); }
      });

      nextBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (idx < images.length - 1) { idx++; updateCarousel(); }
      });

      updateCarousel();
    }

    /* ---------- submit wiring ---------- */

    if (submitEl) {
      submitEl.addEventListener('click', function () { submit(); });
    }

    /* ---------- state actions ---------- */

    function addToBundle(card) {
      var variantId = card.dataset.variantId;
      if (!variantId) return;

      var existing = state.items.get(variantId);
      if (existing) {
        existing.quantity += 1;
      } else {
        state.items.set(variantId, {
          variantId:     variantId,
          productId:     card.dataset.productId,
          productHandle: card.dataset.productHandle,
          title:         card.dataset.productTitle,
          image:         card.dataset.productImage,
          price:         parseInt(card.dataset.variantPrice, 10) || 0,
          quantity:      1
        });
      }
      card.dataset.selected = 'true';
      render();

      var summaryEl = root.querySelector(SELECTORS.summary);
      if (summaryEl && window.matchMedia('(max-width: 959px)').matches) {
        summaryEl.classList.remove('is-item-added');
        void summaryEl.offsetWidth;
        summaryEl.classList.add('is-item-added');
        summaryEl.addEventListener('animationend', function onEnd() {
          summaryEl.classList.remove('is-item-added');
          summaryEl.removeEventListener('animationend', onEnd);
        });
        if (!summaryEl.classList.contains('is-expanded') && !summaryEl.classList.contains('is-peeking')) {
          summaryEl.classList.add('is-peeking');
          setTimeout(function () {
            summaryEl.classList.remove('is-peeking');
          }, 1600);
        }
      }
    }

    function removeOne(variantId) {
      var item = state.items.get(variantId);
      if (!item) return;
      item.quantity -= 1;
      if (item.quantity <= 0) {
        state.items.delete(variantId);
        var card = root.querySelector('[data-variant-id="' + variantId + '"]');
        if (card) delete card.dataset.selected;
      }
      render();
    }

    function addOne(variantId) {
      var item = state.items.get(variantId);
      if (!item) return;
      item.quantity += 1;
      render();
    }

    /* ---------- computed values ---------- */

    function getTotalQuantity() {
      var n = 0;
      state.items.forEach(function (i) { n += i.quantity; });
      return n;
    }

    function getSubtotal() {
      var s = 0;
      state.items.forEach(function (i) { s += i.price * i.quantity; });
      return s;
    }

    function getCurrentTier(qty) {
      var current = null;
      for (var i = 0; i < config.tiers.length; i++) {
        if (qty >= config.tiers[i].threshold) current = config.tiers[i];
        else break;
      }
      return current;
    }

    function getNextTier(qty) {
      for (var i = 0; i < config.tiers.length; i++) {
        if (qty < config.tiers[i].threshold) return config.tiers[i];
      }
      return null;
    }

    function applyPlanDiscount(amount) {
      if (state.planDiscount <= 0) return amount;
      if (state.planDiscountType === 'percentage') {
        return Math.round(amount * (1 - state.planDiscount / 100));
      }
      if (state.planDiscountType === 'fixed_amount') {
        return Math.max(0, amount - state.planDiscount * 100);
      }
      return amount;
    }

    /* ---------- render ---------- */

    function render() {
      renderItems();
      renderProgress();
      renderTotals();
      renderSubmit();
    }

    /* -- items + milestone cards -- */

    function renderItems() {
      if (!itemsEl) return;
      itemsEl.innerHTML = '';

      var qty = getTotalQuantity();

      // Actual selected items
      state.items.forEach(function (item) {
        var li = el('li', 'bab-summary__item');

        if (item.image) {
          var img = new Image();
          img.src = item.image;
          img.alt = '';
          img.loading = 'lazy';
          img.className = 'bab-summary__item-image';
          li.appendChild(img);
        }

        var meta = el('div', 'bab-summary__item-meta');
        meta.appendChild(el('div', 'bab-summary__item-title', item.title));
        meta.appendChild(el('div', 'bab-summary__item-qty',
          formatMoney(item.price, moneyFormat)
        ));
        li.appendChild(meta);

        var qtySelector = el('div', 'bab-summary__item-qty-selector');

        var minusBtn = el('button', 'bab-summary__item-qty-btn', '\u2212');
        minusBtn.type = 'button';
        minusBtn.setAttribute('aria-label', (config.strings.removeOne || 'Remove one') + ' ' + item.title);
        minusBtn.addEventListener('click', function () { removeOne(item.variantId); });

        var qtyVal = el('span', 'bab-summary__item-qty-value', String(item.quantity));

        var plusBtn = el('button', 'bab-summary__item-qty-btn', '+');
        plusBtn.type = 'button';
        plusBtn.setAttribute('aria-label', (config.strings.addOne || 'Add one') + ' ' + item.title);
        plusBtn.addEventListener('click', function () { addOne(item.variantId); });

        qtySelector.appendChild(minusBtn);
        qtySelector.appendChild(qtyVal);
        qtySelector.appendChild(plusBtn);
        li.appendChild(qtySelector);

        itemsEl.appendChild(li);
      });

      // Milestone card only when basket is empty
      if (qty === 0) {
        itemsEl.appendChild(buildMilestoneCard(config.strings.firstProduct));
      }
    }

    function buildMilestoneCard(text, discount) {
      var li      = el('li', 'bab-summary__milestone');
      var imgPlaceholder = el('div', 'bab-summary__milestone-img');
      li.appendChild(imgPlaceholder);

      var textEl = el('p', 'bab-summary__milestone-text', text);
      li.appendChild(textEl);

      // if (discount) {
      //   var badge = el('span', 'bab-purchase-option__badge', '-' + discount + '%');
      //   li.appendChild(badge);
      // }

      return li;
    }

    /* -- progress -- */

    function renderProgress() {
      if (!progressTextEl) return;

      var qty       = getTotalQuantity();
      var nextTier  = getNextTier(qty);
      var curTier   = getCurrentTier(qty);

      var text     = '';
      var discount = '';

      if (qty === 0) {
        // milestone-kaart in data-bab-items toont al de instructie
      } else if (nextTier) {
        var remaining = nextTier.threshold - qty;
        text = interpolate(config.strings.unlockNext, {
          count:    remaining,
          plural:   remaining === 1 ? '' : (config.strings.pluralSuffix != null ? config.strings.pluralSuffix : 'en'),
          discount: nextTier.discount
        });
        discount = '-' + nextTier.discount + '%';
      } else if (curTier) {
        text = config.strings.maxTierReached;
      }

      progressTextEl.textContent = text;
      if (progressDiscEl) progressDiscEl.textContent = discount;

      if (progressSegmentEls.length) {
        progressSegmentEls.forEach(function (seg, i) {
          seg.classList.toggle('is-active', i < qty);
        });
        var bar = progressSegmentEls[0].parentElement;
        if (bar) bar.setAttribute('aria-valuenow', qty);
      } else if (progressFillEl) {
        var maxThreshold = config.tiers.length
          ? config.tiers[config.tiers.length - 1].threshold : 0;
        var pct = maxThreshold > 0 ? Math.min(100, (qty / maxThreshold) * 100) : 0;
        progressFillEl.style.width = pct + '%';
        var bar = progressFillEl.parentElement;
        if (bar) bar.setAttribute('aria-valuenow', Math.round(pct));
      }
    }

    /* -- totals -- */

    function renderTotals() {
      var subtotal      = getSubtotal();
      var qty           = getTotalQuantity();
      var tier          = getCurrentTier(qty);
      var tierDiscount  = tier ? Math.round(subtotal * (tier.discount / 100)) : 0;
      var onetimeTotal  = subtotal - tierDiscount;
      var subTotal      = applyPlanDiscount(onetimeTotal);

      var onetimePriceEl  = root.querySelector(SELECTORS.onetimePrice);
      var subPriceEl      = root.querySelector(SELECTORS.subscriptionPrice);
      var originalPriceEl = root.querySelector(SELECTORS.originalPrice);

      if (onetimePriceEl) onetimePriceEl.textContent = formatMoney(onetimeTotal, moneyFormat);
      if (subPriceEl)     subPriceEl.textContent     = formatMoney(subTotal, moneyFormat);

      if (originalPriceEl) {
        var hasDiscount = tierDiscount > 0;
        originalPriceEl.hidden = !hasDiscount;
        if (hasDiscount) {
          originalPriceEl.textContent = formatMoney(subtotal, moneyFormat);
        }
      }

      var displayTotal = state.purchaseType === 'subscription' ? subTotal : onetimeTotal;
      if (totalEl) totalEl.textContent = formatMoney(displayTotal, moneyFormat);
      var totalBarEl = root.querySelector(SELECTORS.totalBar);
      if (totalBarEl) totalBarEl.textContent = formatMoney(displayTotal, moneyFormat);

      var countBarEl = root.querySelector(SELECTORS.countBar);
      if (countBarEl) {
        var countQty = getTotalQuantity();
        countBarEl.hidden = countQty === 0;
        countBarEl.textContent = countQty + (countQty === 1 ? ' item' : ' items');
      }
    }

    /* -- submit button -- */

    function renderSubmit() {
      if (!submitEl) return;
      submitEl.disabled = getTotalQuantity() < 1;

      if (submitLabelEl && purchaseOptionsEl) {
        var activeTitle = purchaseOptionsEl.querySelector(
          '.bab-purchase-option.is-active .bab-purchase-option__title'
        );
        if (activeTitle) {
          var labelText = Array.from(activeTitle.childNodes)
            .filter(function(n) { return n.nodeType === 3; })
            .map(function(n) { return n.textContent; })
            .join('')
            .trim();
          submitLabelEl.textContent = labelText || activeTitle.textContent.trim();
        }
      }
    }

    /* ---------- cart submit ---------- */

    function buildCartPayload() {
      var parentId     = config.parentProductId;
      var parentHandle = config.parentProductHandle;
      var items        = [];

      state.items.forEach(function (item) {
        var line = {
          id:       parseInt(item.variantId, 10),
          quantity: item.quantity,
          properties: {
            '_box':                   'box-' + parentId,
            '_parent_product_handle': parentHandle,
            '_parent_product_id':     String(parentId)
          }
        };
        if (state.purchaseType === 'subscription' && state.planId) {
          line.selling_plan = state.planId;
        }
        items.push(line);
      });

      return { items: items };
    }

    function setError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg || '';
      errorEl.hidden = !msg;
    }

    function submit() {
      if (state.items.size === 0) return;
      if (state.purchaseType === 'subscription' && !state.planId) {
        setError(config.strings.subscriptionUnavailable || 'Subscription is not available for this bundle.');
        return;
      }

      setError('');
      submitEl.dataset.loading = 'true';
      submitEl.disabled = true;

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify(buildCartPayload())
      })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (body) {
            throw new Error(body.description || body.message || config.strings.errorAdd || 'Could not add to cart.');
          });
        }
        return res.json();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('bab:added', {
          detail: { root: root, mode: state.purchaseType }
        }));
        window.location.href = '/cart';
      })
      .catch(function (err) {
        console.error('[bab] cart add failed', err);
        setError(err.message || config.strings.errorGeneric || 'Something went wrong. Please try again.');
        submitEl.disabled = false;
        delete submitEl.dataset.loading;
      });
    }

    /* ---------- filters ---------- */

    function initFilters() {
      if (!config.filterBlocks || config.filterBlocks.length === 0) return;

      var categoryIndex = {};
      var subcatIndex   = {};

      config.filterBlocks.forEach(function (b) {
        var set = new Set(b.productIds.map(String));
        if (b.type === 'filter_category') categoryIndex[b.handle] = set;
        else subcatIndex[b.handle] = set;
      });

      var activeCat  = 'all';
      var activePill = null;

      var tabBtns  = root.querySelectorAll('[data-bab-filter-tab]');
      var pillBtns = root.querySelectorAll('[data-bab-filter-pill]');
      var cards    = root.querySelectorAll('[data-bab-card]');

      function applyFilters() {
        cards.forEach(function (card) {
          var pid    = card.getAttribute('data-product-id');
          var inCat  = activeCat === 'all' || (categoryIndex[activeCat] && categoryIndex[activeCat].has(pid));
          var inPill = !activePill || (subcatIndex[activePill] && subcatIndex[activePill].has(pid));
          card.classList.toggle('bab-card--hidden', !(inCat && inPill));
        });
      }

      function updatePillVisibility() {
        pillBtns.forEach(function (btn) {
          var handle = btn.getAttribute('data-bab-filter-pill');
          var ids    = subcatIndex[handle];
          if (!ids) { btn.hidden = true; return; }
          var hasVisible = false;
          cards.forEach(function (card) {
            if (!card.classList.contains('bab-card--hidden') && ids.has(card.getAttribute('data-product-id'))) {
              hasVisible = true;
            }
          });
          btn.hidden = !hasVisible;
        });
        if (activePill) {
          var activeBtn = root.querySelector('[data-bab-filter-pill="' + activePill + '"]');
          if (!activeBtn || activeBtn.hidden) {
            activePill = null;
            pillBtns.forEach(function (b) { b.classList.remove('bab__filter-pill--active'); });
          }
        }
      }

      tabBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          activeCat  = btn.getAttribute('data-bab-filter-tab');
          activePill = null;
          tabBtns.forEach(function (b) {
            b.classList.toggle('bab__filter-tab--active', b === btn);
            b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
          });
          pillBtns.forEach(function (b) { b.classList.remove('bab__filter-pill--active'); });
          applyFilters();
          updatePillVisibility();
        });
      });

      pillBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var handle = btn.getAttribute('data-bab-filter-pill');
          if (activePill === handle) {
            activePill = null;
            btn.classList.remove('bab__filter-pill--active');
          } else {
            activePill = handle;
            pillBtns.forEach(function (b) { b.classList.remove('bab__filter-pill--active'); });
            btn.classList.add('bab__filter-pill--active');
          }
          applyFilters();
        });
      });

      updatePillVisibility();
    }

    /* ---------- mobile bottom sheet ---------- */

    function initMobileSheet(summaryEl) {
      if (!summaryEl) return;
      if (!window.matchMedia('(max-width: 959px)').matches) return;

      var backdrop = document.createElement('div');
      backdrop.className = 'bab-summary__backdrop';
      summaryEl.parentElement.insertBefore(backdrop, summaryEl);

      var isExpanded = false;
      var touchStartY = 0;
      var touchDeltaY = 0;

      var savedScrollY = 0;

      function lockScroll() {
        savedScrollY = window.scrollY;
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = '-' + savedScrollY + 'px';
        document.body.style.width = '100%';
      }

      function unlockScroll() {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, savedScrollY);
      }

      function setExpanded(val) {
        isExpanded = val;
        summaryEl.classList.toggle('is-expanded', val);
        backdrop.classList.toggle('is-visible', val);
        summaryEl.style.transform = '';
        val ? lockScroll() : unlockScroll();
      }

      var toggle = summaryEl.querySelector(SELECTORS.sheetToggle);
      if (toggle) toggle.addEventListener('click', function () { setExpanded(!isExpanded); });
      backdrop.addEventListener('click', function () { setExpanded(false); });

      // Drag only on the bar — body content scrolls normally
      var bar = summaryEl.querySelector(SELECTORS.sheetToggle);
      if (bar) {
        bar.addEventListener('touchstart', function (e) {
          touchStartY = e.touches[0].clientY;
          summaryEl.style.transition = 'none';
        }, { passive: true });

        bar.addEventListener('touchmove', function (e) {
          touchDeltaY = e.touches[0].clientY - touchStartY;
          var peekOffset = summaryEl.offsetHeight - 72;
          var baseY = isExpanded ? 0 : peekOffset;
          var newY = Math.max(0, Math.min(peekOffset, baseY + touchDeltaY));
          summaryEl.style.transform = 'translateY(' + newY + 'px)';
        }, { passive: true });

        bar.addEventListener('touchend', function () {
          summaryEl.style.transition = '';
          setExpanded(isExpanded ? touchDeltaY < 80 : touchDeltaY < -50);
          touchDeltaY = 0;
        });
      }
    }

    /* ---------- boot ---------- */

    initPurchaseOptions();
    initFilters();
    render();
    initMobileSheet(root.querySelector(SELECTORS.summary));
  }

  /* =========================================================
     Page boot
     ========================================================= */

  function init() {
    document.querySelectorAll(SELECTORS.root).forEach(createBundle);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector(SELECTORS.root);
    if (root) createBundle(root);
  });

})();
